// src/pages/UrlsPage.jsx
import React, { useEffect, useState, useContext } from 'react';
import {
  Box,
  Breadcrumbs,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Paper,
  TextField,
  Typography,
  Stack,
  Snackbar,
  Alert,
  Divider,
  Popover,
  Menu,
  MenuItem,
  Checkbox,
  TablePagination,
} from '@mui/material';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AddIcon from '@mui/icons-material/Add';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import CodeIcon from '@mui/icons-material/Code';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import PeopleIcon from '@mui/icons-material/People';
import GridViewIcon from '@mui/icons-material/GridView';
import CategoryIcon from '@mui/icons-material/Category';
import LinkIcon from '@mui/icons-material/Link';
import LanguageIcon from '@mui/icons-material/Language';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import ViewInArOutlinedIcon from '@mui/icons-material/ViewInArOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import { request } from '../utils/request';
import { ColorModeContext } from '../index';
import CommonDeleteModal from '../components/Common/CommonDeleteModal';

const DOMAIN_REGEX = /^((https?:\/\/)?([\w-]+\.)+[\w-]{2,})(\/.*)?$/i;

export default function UrlsPage() {
  const { mode } = useContext(ColorModeContext);
  const isDark = mode === 'dark';

  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isSuperAdmin = currentUser.superadmin === true;
  const userPermissions = currentUser.permissions || [];

  const checkPerm = (action) => {
    if (isSuperAdmin) return true;
    return userPermissions.some((permission) => {
      const normalizedPermission = String(permission).toLowerCase();
      return (
        (normalizedPermission.includes('whitelist urls') ||
          normalizedPermission.includes('protocol rules') ||
          normalizedPermission.includes('urls')) &&
        normalizedPermission.includes(action.toLowerCase())
      );
    });
  };

  const canCreate = checkPerm('write');
  const canEdit = checkPerm('update');
  const canDelete = checkPerm('delete');

  const [categories, setCategories] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [open, setOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState({
    id: null,
    category_Name: '',
    Urls: [],
    newUrlInput: '',
  });

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletingCat, setDeletingCat] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState('');
  const [snackSeverity, setSnackSeverity] = useState('success');

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => setPage(0), [categories.length]);

  const paginatedCategories = categories.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
  const handleChangePage = (event, nextPage) => setPage(nextPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10));
    setPage(0);
  };

  const [anchorEl, setAnchorEl] = useState(null);
  const [menuRowId, setMenuRowId] = useState(null);

  const handleMenuOpen = (event, id) => {
    setAnchorEl(event.currentTarget);
    setMenuRowId(id);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuRowId(null);
  };

  const onMenuEdit = (cat) => {
    if (!canEdit) return;
    handleMenuClose();
    openEdit(cat);
  };

  const onMenuCopy = (cat) => {
    handleMenuClose();
    copyUrls(cat.Urls);
  };

  const onMenuDelete = (cat) => {
    if (!canDelete) return;
    handleMenuClose();
    confirmDelete(cat);
  };

  const showSnack = (msg, severity = 'success') => {
    setSnackMsg(msg);
    setSnackSeverity(severity);
    setSnackOpen(true);
  };

  const handleSnackClose = (e, reason) => {
    if (reason === 'clickaway') return;
    setSnackOpen(false);
  };

  const fetchCategories = async () => {
    try {
      const data = await request('urls', { method: 'get' });
      const arr = Array.isArray(data) ? data : [];
      const normalized = arr.map((d) => ({
        id: d._id,
        category_Name: d.category_Name,
        Urls: Array.isArray(d.Urls) ? d.Urls : [],
        createdAt: d.createdAt,
        updatedAt: d.updatedAt,
      }));
      setCategories(normalized);
    } catch (err) {
      console.error('fetchCategories error', err);
      showSnack('Failed to load categories.', 'error');
      setCategories([]);
    }
  };

  const openAdd = () => {
    setForm({
      id: null,
      category_Name: '',
      Urls: [],
      newUrlInput: '',
    });
    setIsEditing(false);
    setOpen(true);
  };

  const openEdit = (cat) => {
    setForm({
      id: cat.id,
      category_Name: cat.category_Name,
      Urls: [...cat.Urls],
      newUrlInput: '',
    });
    setIsEditing(true);
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const addSingleUrl = () => {
    const v = String(form.newUrlInput || '').trim();
    if (!v) return;
    const normalized = v.replace(/^https?:\/\//i, '').replace(/\/+$/, '');
    if (!DOMAIN_REGEX.test(normalized) && !/^[\w.-]+\.[a-z]{2,}$/i.test(normalized)) {
      showSnack('Invalid domain/URL format.', 'error');
      return;
    }
    if (form.Urls.includes(normalized)) {
      showSnack('URL already added to this category.', 'warning');
      return;
    }
    setForm((p) => ({ ...p, Urls: [...p.Urls, normalized], newUrlInput: '' }));
  };

  const removeUrlAt = (index) => {
    setForm((p) => {
      const copy = [...p.Urls];
      copy.splice(index, 1);
      return { ...p, Urls: copy };
    });
  };

  const handleSubmit = async () => {
    if (!isEditing && !canCreate) return;
    if (isEditing && !canEdit) return;

    const name = String(form.category_Name || '').trim();
    if (!name) {
      showSnack('Category name is required.', 'error');
      return;
    }

    const normalizedUrls = Array.from(new Set((form.Urls || []).map((u) => String(u).trim()).filter(Boolean)));

    if (normalizedUrls.length === 0) {
      showSnack('Please add at least one URL or Domain.', 'error');
      return;
    }

    try {
      if (isEditing && form.id) {
        await request(`urls/${form.id}`, {
          method: 'put',
          body: { category_Name: name, Urls: normalizedUrls },
        });
        showSnack('Category updated.', 'success');
      } else {
        await request('urls', { method: 'post', body: { category_Name: name, Urls: normalizedUrls } });
        showSnack('Category created.', 'success');
      }
      handleClose();
      fetchCategories();
    } catch (err) {
      console.error('save error', err);
      const msg = err?.response?.data?.message || err?.message || 'Failed to save category.';
      showSnack(msg, 'error');
    }
  };

  const confirmDelete = (cat) => {
    setDeletingCat(cat);
    setDeleteOpen(true);
  };

  const cancelDelete = () => {
    setDeletingCat(null);
    setDeleteOpen(false);
    setDeleting(false);
  };

  const handleConfirmDelete = async () => {
    if (!deletingCat || !deletingCat.id) return;
    setDeleting(true);
    try {
      await request(`urls`, { method: 'delete', params: deletingCat.id });
      showSnack('Category deleted.', 'success');
      cancelDelete();
      fetchCategories();
    } catch (err) {
      console.error('delete error', err);
      showSnack('Failed to delete category.', 'error');
      setDeleting(false);
    }
  };

  const [moreAnchorEl, setMoreAnchorEl] = useState(null);
  const [moreRowId, setMoreRowId] = useState(null);
  const [moreUrls, setMoreUrls] = useState([]);

  const handleMoreOpen = (event, rowId, urls) => {
    setMoreAnchorEl(event.currentTarget);
    setMoreRowId(rowId);
    setMoreUrls(urls || []);
  };

  const handleMoreClose = () => {
    setMoreAnchorEl(null);
    setMoreRowId(null);
    setMoreUrls([]);
  };

  const renderUrlChips = (arr = [], catId = '', options = {}) => {
    if (!arr || arr.length === 0) {
      return <Typography sx={{ color: isDark ? '#9CA3AF' : 'text.secondary' }}>—</Typography>;
    }

    const preview = arr.slice(0, 3);
    const remainingCount = arr.length - preview.length;

    return (
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        {preview.map((u, i) => (
          <Chip
            key={i}
            size="small"
            label={u}
            onDelete={
              options.allowDelete
                ? () => options.onDelete && options.onDelete(i)
                : undefined
            }
            sx={{
              maxWidth: 220,
              textOverflow: 'ellipsis',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              m: 0.5,
              bgcolor: isDark ? '#374151' : '#F3F4F6',
              color: isDark ? '#D1D5DB' : '#4B5563',
              border: 'none',
              borderRadius: '6px',
              fontWeight: 500,
              fontSize: '0.75rem',
              '& .MuiChip-label': { px: 1.5 },
            }}
          />
        ))}

        {remainingCount > 0 && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              userSelect: 'none',
              ml: 1,
              color: '#3B82F6',
              bgcolor: isDark ? 'rgba(59, 130, 246, 0.1)' : '#EFF6FF',
              px: 1,
              py: 0.5,
              borderRadius: '6px',
              transition: 'all 0.2s',
              '&:hover': {
                bgcolor: isDark ? 'rgba(59, 130, 246, 0.2)' : '#DBEAFE',
              }
            }}
            onClick={(e) => handleMoreOpen(e, catId || 'row', arr)}
          >
            <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
              +{remainingCount} more
            </Typography>
            <KeyboardArrowDownIcon fontSize="small" sx={{ ml: 0.5, fontSize: '1rem' }} />
          </Box>
        )}
      </Stack>
    );
  };

  const copyUrls = async (arr = []) => {
    try {
      await navigator.clipboard.writeText((arr || []).join('\n'));
      showSnack('URLs copied to clipboard.', 'success');
    } catch {
      showSnack('Unable to copy to clipboard.', 'error');
    }
  };

  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const isAllSelected = categories.length > 0 && selectedIds.length === categories.length;

  const toggleSelectAll = () => {
    if (isAllSelected) {
      setSelectedIds([]);
    } else {
      setSelectedIds(categories.map((c) => c.id));
    }
  };

  const handleEditSelected = () => {
    if (!canEdit) return;
    if (selectedIds.length !== 1) return;
    const cat = categories.find((c) => c.id === selectedIds[0]);
    if (cat) {
      openEdit(cat);
      setSelectedIds([]);
    }
  };

  const handleDeleteSelected = () => {
    if (!canDelete) return;
    if (selectedIds.length === 0) return;
    setBulkDeleteOpen(true);
  };

  const handleCancelBulkDelete = () => {
    setBulkDeleteOpen(false);
    setBulkDeleting(false);
  };

  const handleConfirmBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    setBulkDeleting(true);
    try {
      await Promise.all(
        selectedIds.map((id) => request('urls', { method: 'delete', params: id }))
      );
      showSnack(`${selectedIds.length} category(ies) deleted.`, 'success');
      setSelectedIds([]);
      await fetchCategories();
      handleCancelBulkDelete();
    } catch (err) {
      console.error('Bulk delete error', err);
      showSnack('Failed to delete selected categories.', 'error');
      setBulkDeleting(false);
    }
  };

  const inputStyle = {
    width: '100%',
    '& .MuiInputBase-root': { 
      backgroundColor: isDark ? '#111827' : '#fff',
      borderRadius: '6px',
    },
    '& .MuiInputBase-input': { 
      color: isDark ? '#F9FAFB' : 'inherit' 
    },
    '& input::placeholder': {
      color: '#9CA3AF',
      opacity: 1,
    },
    '& .MuiInputLabel-root': { 
      color: isDark ? '#9CA3AF' : 'inherit',
      backgroundColor: isDark ? '#111827' : '#fff',
      padding: '0 4px',
      marginLeft: '-4px',
    },
    '& .MuiOutlinedInput-notchedOutline': { 
      borderColor: isDark ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0,0,0,0.23)' 
    },
    '&:hover .MuiOutlinedInput-notchedOutline': { 
      borderColor: isDark ? 'rgba(255, 255, 255, 0.3) !important' : 'rgba(0,0,0,0.87) !important' 
    },
    '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#3B82F6 !important',
      borderWidth: '2px',
    },
    '& .MuiSvgIcon-root': { color: '#9CA3AF' }
  };

  const getCategoryIcon = (name = '') => {
    const n = name.toLowerCase();
    if (n.includes('entertainment') || n.includes('gaming')) {
      return { icon: SportsEsportsIcon, color: '#3B82F6' };
    }
    if (n.includes('developer') || n.includes('tech')) {
      return { icon: CodeIcon, color: '#8B5CF6' };
    }
    if (n.includes('finance') || n.includes('payment')) {
      return { icon: AccountBalanceIcon, color: '#10B981' };
    }
    if (n.includes('commerce') || n.includes('market')) {
      return { icon: ShoppingCartIcon, color: '#F97316' };
    }
    if (n.includes('video') || n.includes('streaming')) {
      return { icon: PlayArrowIcon, color: '#EF4444' };
    }
    if (n.includes('news') || n.includes('media')) {
      return { icon: NewspaperIcon, color: '#A855F7' };
    }
    if (n.includes('social') || n.includes('communication')) {
      return { icon: PeopleIcon, color: '#3B82F6' };
    }
    if (n.includes('productivity') || n.includes('collaboration')) {
      return { icon: GridViewIcon, color: '#FBBF24' };
    }
    return { icon: CategoryIcon, color: '#6B7280' };
  };

  return (
    <Box p={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Link
              href="/home"
              underline="hover"
              style={{
                textDecoration: "none",
                color: isDark ? "#90caf9" : "#2d9cdb",
                fontSize: "0.95rem",
                transition: "color 0.2s ease",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.color = isDark ? "#fff" : "#0b1d51")}
              onMouseLeave={(e) => (e.currentTarget.style.color = isDark ? "#90caf9" : "#2d9cdb")}
            >
              Dashboard
            </Link>

            <Typography sx={{ fontSize: "0.95rem", color: isDark ? '#fff' : 'inherit' }}>/</Typography>
            <Typography sx={{ fontSize: "0.95rem", color: isDark ? "#fff" : "black" }}>
              Protocol Rules
            </Typography>
          </Stack>
        </Box>

        {canCreate && (
          <Button
            variant="contained"
            onClick={openAdd}
            sx={{
              backgroundColor: '#2563EB',
              color: '#fff',
              px: 2,
              py: 0.8,
              '&:hover': { backgroundColor: '#1d4ed8', color: 'white' }
            }}
          >
            Add category
          </Button>
        )}
      </Stack>

      {selectedIds.length > 0 && (
        <Box mb={2} display="flex" justifyContent="flex-start" alignItems="center" gap={2}>
          <Typography variant="body2" sx={{ color: isDark ? '#fff' : 'inherit' }}>
            {selectedIds.length} selected
          </Typography>

          {canEdit && (
            <Button
              size="small"
              variant="outlined"
              onClick={handleEditSelected}
              disabled={selectedIds.length !== 1}
              sx={{ borderColor: isDark ? '#fff' : undefined, color: isDark ? '#fff' : undefined }}
            >
              Edit
            </Button>
          )}

          {canDelete && (
            <Button
              size="small"
              variant="contained"
              onClick={handleDeleteSelected}
              sx={{ backgroundColor: '#dc3545', color: '#fff', '&:hover': { backgroundColor: '#b71c1c' } }}
            >
              Delete
            </Button>
          )}

          <Button
            size="small"
            onClick={() => setSelectedIds([])}
            sx={{ color: isDark ? '#fff' : undefined }}
          >
            Clear
          </Button>
        </Box>
      )}

      <Paper 
        elevation={0} 
        sx={{ 
          mb: 2, 
          height: 'calc(100vh - 170px)',
          backgroundColor: isDark ? '#111827' : '#ffffff',
          borderRadius: '12px',
          border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid #E5E7EB',
          overflow: 'hidden'
        }}
      >
        <Box 
          sx={{ 
            height: 'calc(100% - 60px)', 
            overflow: 'auto', 
            width: '100%',
            scrollbarWidth: 'thin',
            scrollbarColor: isDark ? 'rgba(255, 255, 255, 0.2) transparent' : 'rgba(0, 0, 0, 0.2) transparent',
            '&::-webkit-scrollbar': { width: '6px', height: '6px' },
            '&::-webkit-scrollbar-track': { background: 'transparent' },
            '&::-webkit-scrollbar-thumb': { background: isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)', borderRadius: '10px' },
            '&::-webkit-scrollbar-thumb:hover': { background: isDark ? 'rgba(255, 255, 255, 0.35)' : 'rgba(0, 0, 0, 0.35)' }
          }}
        >
          <Box component="table" width="100%" sx={{ borderCollapse: 'collapse', minWidth: 1000 }}>
            <Box component="thead">
              <Box component="tr">
                <Box
                  component="th"
                  py={2}
                  px={1}
                  sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                    backgroundColor: isDark ? '#111827' : '#ffffff',
                    borderBottom: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid #E5E7EB',
                    width: 48,
                    textAlign: 'center'
                  }}
                >
                  <Checkbox
                    size="small"
                    checked={isAllSelected}
                    indeterminate={selectedIds.length > 0 && !isAllSelected}
                    onChange={(e) => toggleSelectAll(e.target.checked)}
                    inputProps={{ 'aria-label': 'select all categories' }}
                    sx={{ color: isDark ? 'rgba(255,255,255,0.7)' : '#6B7280' }}
                  />
                </Box>

                {[
                  { label: '#', width: 40, align: 'left' },
                  { label: 'Category Name', width: 220, align: 'left' },
                  { label: 'URLs (preview)', width: 500, align: 'left' },
                  { label: 'Count', width: 80, align: 'center' },
                  { label: 'Actions', width: 80, align: 'center' }
                ].map((col, i) => (
                  <Box
                    component="th"
                    key={i}
                    py={2}
                    px={1}
                    textAlign={col.align}
                    sx={{
                      position: 'sticky',
                      top: 0,
                      zIndex: 2,
                      width: col.width,
                      backgroundColor: isDark ? '#111827' : '#ffffff',
                      color: isDark ? '#F9FAFB' : '#0B1D51',
                      borderBottom: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid #E5E7EB',
                      fontFamily: 'Arial, sans-serif',
                      fontWeight: 700,
                      fontSize: '13px',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {col.label}
                  </Box>
                ))}
              </Box>
            </Box>

            <Box component="tbody">
              {categories.length === 0 && (
                <Box component="tr">
                  <Box component="td" colSpan={6} p={3} textAlign="center" sx={{ color: isDark ? '#9CA3AF' : 'inherit' }}>
                    No categories yet. Click "Add Category" to create one.
                  </Box>
                </Box>
              )}
              {paginatedCategories.map((c, idx) => (
                <Box 
                  component="tr" 
                  key={c.id || idx}
                  sx={{
                    borderBottom: isDark ? '1px solid rgba(255,255,255,0.05)' : '1px solid #F3F4F6',
                    '&:hover': { backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : '#F9FAFB' }
                  }}
                >
                  <Box component="td" py={2} px={1} textAlign="center" sx={{ verticalAlign: 'middle' }}>
                    <Checkbox
                      size="small"
                      checked={selectedIds.includes(c.id)}
                      onChange={() => toggleSelect(c.id)}
                      inputProps={{ 'aria-label': `select category ${c.category_Name}` }}
                      sx={{ color: isDark ? 'rgba(255,255,255,0.7)' : '#9CA3AF' }}
                    />
                  </Box>
                  <Box component="td" py={2} px={1} sx={{ color: isDark ? '#D1D5DB' : '#4B5563', verticalAlign: 'middle', fontWeight: 500 }}>
                    {page * rowsPerPage + idx + 1}
                  </Box>
                  <Box component="td" py={2} px={1} sx={{ verticalAlign: 'middle' }}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      {(() => {
                        const iconData = getCategoryIcon(c.category_Name);
                        const IconComponent = iconData.icon;
                        return (
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              minWidth: 32,
                              borderRadius: '8px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: isDark ? `${iconData.color}20` : `${iconData.color}15`,
                              color: iconData.color,
                            }}
                          >
                            <IconComponent fontSize="small" />
                          </Box>
                        );
                      })()}
                      <Typography sx={{ color: isDark ? '#F9FAFB' : '#111827', fontWeight: 600, fontSize: '0.875rem' }}>
                        {c.category_Name}
                      </Typography>
                    </Stack>
                  </Box>
                  <Box component="td" py={2} px={1} sx={{ verticalAlign: 'middle' }}>
                    {renderUrlChips(c.Urls, c.id)}
                  </Box>
                  <Box component="td" py={2} px={1} textAlign="center" sx={{ color: isDark ? '#D1D5DB' : '#4B5563', verticalAlign: 'middle', fontWeight: 500 }}>
                    {Array.isArray(c.Urls) ? c.Urls.length : 0}
                  </Box>
                  <Box component="td" py={2} px={1} textAlign="center" sx={{ verticalAlign: 'middle' }}>
                    <IconButton
                      size="small"
                      aria-controls={menuRowId === c.id ? `row-menu-${c.id}` : undefined}
                      aria-haspopup="true"
                      onClick={(e) => handleMenuOpen(e, c.id)}
                      sx={{ color: isDark ? '#9CA3AF' : '#6B7280' }}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>

                    <Menu
                      id={`row-menu-${c.id}`}
                      anchorEl={anchorEl}
                      open={Boolean(anchorEl) && menuRowId === c.id}
                      onClose={handleMenuClose}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                      PaperProps={{ 
                        sx: { 
                          backgroundColor: isDark ? '#111827' : '#fff', 
                          color: isDark ? '#F9FAFB' : 'inherit', 
                          backgroundImage: 'none' 
                        } 
                      }}
                    >
                      {canEdit && <MenuItem onClick={() => onMenuEdit(c)}>Edit</MenuItem>}
                      <MenuItem onClick={() => onMenuCopy(c)}>Copy URLs</MenuItem>
                      {canDelete && <MenuItem onClick={() => onMenuDelete(c)} sx={{ color: '#d32f2f' }}>Delete</MenuItem>}
                    </Menu>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
        <TablePagination
          component="div"
          count={categories.length}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[25, 50, 100]}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            flexShrink: 0,
            borderTop: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid #E5E7EB',
            color: isDark ? '#9CA3AF' : '#4B5563',
            '& .MuiTablePagination-selectIcon': {
              color: isDark ? '#9CA3AF' : '#4B5563',
            },
            '& .MuiTablePagination-actions button': {
              color: isDark ? '#9CA3AF' : '#4B5563',
            }
          }}
        />
      </Paper>

      <Popover
        open={Boolean(moreAnchorEl)}
        anchorEl={moreAnchorEl}
        onClose={handleMoreClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ 
          sx: { 
            p: 2, 
            maxWidth: 420, 
            maxHeight: 260,
            backgroundColor: isDark ? '#111827' : '#ffffff', 
            color: isDark ? '#F9FAFB' : 'inherit', 
            backgroundImage: 'none', 
            border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid #E5E7EB',
            borderRadius: '12px',
            boxShadow: isDark ? '0 4px 20px rgba(0,0,0,0.5)' : '0 4px 20px rgba(0,0,0,0.08)',
            scrollbarWidth: 'thin',
            scrollbarColor: isDark ? 'rgba(255, 255, 255, 0.2) transparent' : 'rgba(0, 0, 0, 0.2) transparent',
            '&::-webkit-scrollbar': { width: '8px' },
            '&::-webkit-scrollbar-track': { background: 'transparent' },
            '&::-webkit-scrollbar-thumb': { background: isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)', borderRadius: '10px' },
            '&::-webkit-scrollbar-thumb:hover': { background: isDark ? 'rgba(255, 255, 255, 0.35)' : 'rgba(0, 0, 0, 0.35)' }
          } 
        }}
      >
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {moreUrls.map((u, i) => (
            <Chip
              key={i}
              label={u}
              size="small"
              sx={{ 
                m: 0, 
                bgcolor: isDark ? '#1f2937' : '#ffffff',
                color: isDark ? '#D1D5DB' : '#4B5563',
                border: isDark ? '1px solid rgba(255,255,255,0.15)' : '1px solid #E5E7EB',
                borderRadius: '16px',
                fontWeight: 500,
                fontSize: '0.8rem',
                '& .MuiChip-label': { px: 1.5 },
              }}
            />
          ))}
        </Box>
      </Popover>

      <Dialog 
        open={open} 
        onClose={handleClose} 
        fullWidth 
        maxWidth={false}
        sx={{
          '& .MuiDialog-paper': {
            width: 'min(960px, calc(100vw - 32px)) !important',
            maxWidth: '960px !important',
            maxHeight: 'calc(100vh - 64px) !important',
            borderRadius: '16px',
            backgroundColor: isDark ? '#111827' : '#fff', 
            color: isDark ? '#F9FAFB' : 'inherit',
            backgroundImage: 'none',
            border: isDark ? '1px solid rgba(255,255,255,0.1)' : 'none',
            boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
            overflow: 'hidden'
          }
        }}
        PaperProps={{ className: 'whitelist-dialog' }}
        BackdropProps={{
          sx: {
            backdropFilter: 'blur(4px)',
            backgroundColor: 'rgba(0, 0, 0, 0.4)'
          }
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
          {/* LEFT COLUMN */}
          <Box sx={{ 
            width: { xs: '100%', md: '340px' }, 
            bgcolor: isDark ? '#1F2937' : '#F0F7FF', 
            p: 3,
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column',
            position: 'relative',
            overflow: 'hidden'
          }}>
            <Box sx={{ width: 48, height: 48, bgcolor: isDark ? 'rgba(59,130,246,0.2)' : '#E0F2FE', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
              <LinkIcon sx={{ color: '#0EA5E9', fontSize: 24, transform: 'rotate(-45deg)' }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, color: isDark ? '#F9FAFB' : '#111827' }}>
              {isEditing ? 'Edit Category' : 'Create Category'}
            </Typography>
            <Typography variant="body2" sx={{ color: isDark ? '#9CA3AF' : '#4B5563', mb: 3, lineHeight: 1.6 }}>
              Organize and whitelist domains or URLs by category to control browser access.
            </Typography>

            <Stack spacing={2}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ width: 40, height: 40, minWidth: 40, borderRadius: '50%', bgcolor: '#E0F2FE', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <ViewInArOutlinedIcon sx={{ color: '#0EA5E9', fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', color: isDark ? '#F9FAFB' : '#111827' }}>Group URLs</Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: isDark ? '#9CA3AF' : '#6B7280' }}>Add domains or specific URLs under a category.</Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ width: 40, height: 40, minWidth: 40, borderRadius: '50%', bgcolor: '#DCFCE7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <ShieldOutlinedIcon sx={{ color: '#16A34A', fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', color: isDark ? '#F9FAFB' : '#111827' }}>Manage Access</Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: isDark ? '#9CA3AF' : '#6B7280' }}>Helps enforce safe and controlled browsing.</Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ width: 40, height: 40, minWidth: 40, borderRadius: '50%', bgcolor: '#F3E8FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <SettingsOutlinedIcon sx={{ color: '#9333EA', fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', color: isDark ? '#F9FAFB' : '#111827' }}>Flexible Rules</Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: isDark ? '#9CA3AF' : '#6B7280' }}>Add single domains or full URL paths as needed.</Typography>
                </Box>
              </Box>
            </Stack>

            {/* Graphic at the bottom */}
            <Box sx={{ 
              '@media (max-height: 850px)': {
                display: 'none'
              },
              position: 'absolute', bottom: -10, left: -10, right: 0, height: 200, 
              zIndex: 0, pointerEvents: 'none'
            }}>
              {/* Back window */}
              <Box sx={{ position: 'absolute', bottom: 60, left: 110, width: 140, height: 100, bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.4)', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(255,255,255,0.8)', transform: 'rotate(-5deg)' }} />
              {/* Middle window */}
              <Box sx={{ position: 'absolute', bottom: 35, left: 75, width: 140, height: 100, bgcolor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.6)', borderRadius: '8px', boxShadow: '0 8px 24px rgba(0,0,0,0.08)', border: isDark ? '1px solid rgba(255,255,255,0.15)' : '1px solid rgba(255,255,255,0.9)', transform: 'rotate(-5deg)' }} />
              {/* Front window */}
              <Box sx={{ position: 'absolute', bottom: 10, left: 40, width: 150, height: 110, bgcolor: isDark ? '#374151' : '#ffffff', borderRadius: '8px', boxShadow: '0 12px 32px rgba(0,0,0,0.12)', border: isDark ? '1px solid rgba(255,255,255,0.2)' : '1px solid #E5E7EB', overflow: 'hidden', transform: 'rotate(-5deg)' }}>
                {/* Browser header */}
                <Box sx={{ height: 24, bgcolor: '#2563EB', display: 'flex', alignItems: 'center', px: 1, gap: 0.5 }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.8)' }} />
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.8)' }} />
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.8)' }} />
                </Box>
                {/* Browser body */}
                <Box sx={{ height: 'calc(100% - 24px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <LinkIcon sx={{ color: '#2563EB', fontSize: 40, transform: 'rotate(-45deg)' }} />
                </Box>
              </Box>
              {/* Bottom soft gradient for blending into the panel */}
              <Box sx={{ position: 'absolute', bottom: 10, left: 0, right: 0, height: 80, background: isDark ? 'linear-gradient(to top, #1F2937, transparent)' : 'linear-gradient(to top, #F0F7FF, transparent)', transform: 'rotate(-5deg) scale(1.2)' }} />
            </Box>
          </Box>
          
          {/* RIGHT COLUMN */}
          <Box sx={{ flex: 1, p: 3, display: 'flex', flexDirection: 'column', position: 'relative', overflowY: 'auto' }}>
            <IconButton onClick={handleClose} sx={{ position: 'absolute', right: 12, top: 12, color: isDark ? '#9CA3AF' : '#4B5563', zIndex: 10 }}>
              <CloseIcon />
            </IconButton>

            <Stack spacing={1.5} sx={{ flex: 1, zIndex: 1 }}>
              <Box>
                <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', mb: 1, color: isDark ? '#F9FAFB' : '#111827' }}>
                  Category Name <span style={{ color: '#EF4444' }}>*</span>
                </Typography>
                <TextField
                  size="small"
                  name="category_Name"
                  autoComplete="off"
                  value={form.category_Name}
                  onChange={handleFormChange}
                  fullWidth
                  placeholder="Enter category name"
                  sx={inputStyle}
                  InputProps={{
                    startAdornment: <LocalOfferOutlinedIcon sx={{ color: '#9CA3AF', mr: 1, fontSize: 20 }} />
                  }}
                />
                <Typography sx={{ fontSize: '0.75rem', color: isDark ? '#9CA3AF' : '#6B7280', mt: 0.5 }}>
                  Use a clear and descriptive name for this category (e.g. Social Media, Productivity).
                </Typography>
              </Box>

              <Box>
                <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', mb: 1, color: isDark ? '#F9FAFB' : '#111827' }}>
                  Add URL / Domain <span style={{ color: '#EF4444' }}>*</span>
                </Typography>
                <Stack direction="row" spacing={1}>
                  <TextField
                    size="small"
                    name="newUrlInput"
                    autoComplete="off"
                    value={form.newUrlInput}
                    onChange={handleFormChange}
                    placeholder="example.com or https://example.com/path"
                    fullWidth
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addSingleUrl();
                      }
                    }}
                    sx={inputStyle}
                    InputProps={{
                      startAdornment: <LinkIcon sx={{ color: '#9CA3AF', mr: 1, fontSize: 20, transform: 'rotate(-45deg)' }} />
                    }}
                  />
                  <Button 
                    variant="contained" 
                    onClick={addSingleUrl} 
                    startIcon={<AddIcon />}
                    sx={{ color: '#fff', backgroundColor: '#2563EB', px: 3, borderRadius: '8px', boxShadow: 'none', '&:hover': { backgroundColor: '#1d4ed8', boxShadow: 'none' } }}
                  >
                    Add
                  </Button>
                </Stack>
                <Typography sx={{ fontSize: '0.75rem', color: isDark ? '#9CA3AF' : '#6B7280', mt: 0.5 }}>
                  Enter a single domain or full URL path and click Add to include it in this category.
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', mb: 1, color: isDark ? '#F9FAFB' : '#111827' }}>
                  Current URLs ({form.Urls.length})
                </Typography>
                <Paper 
                  variant="outlined" 
                  sx={{ 
                    p: form.Urls.length ? 1 : 2, 
                    minHeight: 100,
                    maxHeight: 140,
                    overflowY: 'auto',
                    borderColor: isDark ? 'rgba(255,255,255,0.15)' : '#E5E7EB', 
                    bgcolor: isDark ? '#1F2937' : '#ffffff',
                    borderRadius: '8px',
                    display: 'flex',
                    ...(form.Urls.length 
                      ? { flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'flex-start', flexWrap: 'wrap', alignContent: 'flex-start' }
                      : { flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }
                    )
                  }}
                >
                  {form.Urls.length ? (
                    renderUrlChips(form.Urls, form.id || 'form', { allowDelete: true, onDelete: removeUrlAt })
                  ) : (
                    <Box sx={{ textAlign: 'center' }}>
                      <Box sx={{ width: 44, height: 44, borderRadius: '50%', bgcolor: isDark ? '#374151' : '#F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 1 }}>
                        <LanguageIcon sx={{ color: isDark ? '#9CA3AF' : '#9CA3AF', fontSize: 24 }} />
                      </Box>
                      <Typography sx={{ fontWeight: 600, color: isDark ? '#F9FAFB' : '#111827' }}>No URLs added yet.</Typography>
                      <Typography sx={{ fontSize: '0.8rem', color: isDark ? '#9CA3AF' : '#6B7280' }}>Start by adding a domain or URL above.</Typography>
                    </Box>
                  )}
                </Paper>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', p: 1.5, bgcolor: isDark ? 'rgba(99,102,241,0.1)' : '#F5F3FF', borderRadius: '8px', gap: 1.5 }}>
                <InfoOutlinedIcon sx={{ color: '#8B5CF6', fontSize: 20 }} />
                <Typography variant="body2" sx={{ color: isDark ? '#A78BFA' : '#6D28D9', fontSize: '0.75rem' }}>
                  Tip: store domain only (example.com) for broader matching, or include paths if you need fine-grained entries.
                </Typography>
              </Box>
            </Stack>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2, pt: 2, borderTop: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid #E5E7EB' }}>
              <Button onClick={handleClose} variant="outlined" sx={{ color: isDark ? '#F9FAFB' : '#4B5563', borderColor: isDark ? 'rgba(255,255,255,0.2)' : '#D1D5DB', borderRadius: '8px', px: 3, '&:hover': { borderColor: '#9CA3AF', backgroundColor: 'transparent' } }}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                startIcon={<AddIcon />}
                sx={{ color: '#fff', backgroundColor: '#2563EB', borderRadius: '8px', px: 3, boxShadow: 'none', '&:hover': { backgroundColor: '#1d4ed8', boxShadow: 'none' } }}
              >
                {isEditing ? 'Update' : 'Create Category'}
              </Button>
            </Box>
          </Box>
        </Box>
      </Dialog>

      <CommonDeleteModal
        open={deleteOpen}
        onClose={cancelDelete}
        onConfirm={handleConfirmDelete}
        isDeleting={deleting}
        entityName={deletingCat ? deletingCat.category_Name : ''}
        entityType="Category"
      />

      <CommonDeleteModal
        open={bulkDeleteOpen}
        onClose={handleCancelBulkDelete}
        onConfirm={handleConfirmBulkDelete}
        isDeleting={bulkDeleting}
        entityType="Category"
        isBulk={true}
        bulkCount={selectedIds.length}
      />

      <Snackbar open={snackOpen} autoHideDuration={3000} onClose={handleSnackClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert onClose={handleSnackClose} severity={snackSeverity} sx={{ width: '100%' }}>
          {snackMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
}